import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { API_ROUTES } from '../constants';
import { HTTP_CONFIG, HTTP_CONFIG_BATCH } from '../defineTdm';
import {
  formatFullDateTime,
  issueFilter,
  timelineEventDetailHeaderFilter,
  timelineEventIssueFilter,
  timelineEventTypeFilter,
} from '../lib/filters';
import { getProperty } from '../fsConfig';
import { progressHide, progressIncrement, progressShow } from './batchProgress';
import { TaskPayload, FinalResults } from './types';
import { TimelineEvent, Issue } from '../headersegment/interfaces';

export const IssueService = {
  startBatchTasks(issues: Issue[], taskPayload: TaskPayload, route: string, issuesUpdated?: any) {
    const maxValue = issues.length;
    const spliceEndIndex =
      issues.length <= 5 ? 1 : issues.length <= 125 ? 5 : 25;
    const issuesSets: Issue[][] = [];

    while (issues.length) {
      issuesSets.push(issues.splice(0, spliceEndIndex));
    }
    
    progressShow({
      processedStr: 'actions processed',
      maxValue: maxValue,
    });

    const finalResults: FinalResults = {
      requests: 0,
      succeeded: 0,
      failed: 0,
      failedItems: [],
    };

    let promise: Promise<void | FinalResults> = Promise.resolve();
    issuesSets.forEach(issuesSet => {
      promise = promise.then(() =>
        this.batchTask(
          { ...taskPayload, issues: issuesSet },
          route,
          finalResults,
        ),
      );
    });

    promise.then((resp) => {
      progressHide(() => issuesUpdated(resp));
    });
  },

  async batchTask(
    payload: TaskPayload,
    route: string,
    finalResults: FinalResults,
  ) {
    try {
      const response: AxiosResponse<any> = await axios.post(
        route,
        payload,
        HTTP_CONFIG_BATCH,
      );
      const { requests, succeeded, failed, failedItems } = response.data;

      finalResults.requests += requests;
      finalResults.succeeded += succeeded;
      finalResults.failed += failed;
      finalResults.failedItems.push(...failedItems);
      progressIncrement({ numComplete: requests });
    } catch (error: any) {
      finalResults.requests += payload.issues?.length ?? 0;
      finalResults.failed += payload.issues?.length ?? 0;
      progressIncrement({ numComplete: error?.response?.data?.requests || 0 });
    }

    return finalResults;
  },

  assign(issues: Issue[], assignToId: string | null, batchReq?: boolean, issuesUpdated?: any) {
    const port =
      window.location.port === '443' || window.location.port === '80'
        ? ''
        : `:${window.location.port}`;
    const issueLink = `${window.location.protocol}//${window.location.host}${port}/trip/%TRIP_ID%#issue-%ISSUE_ID%`;

    if (batchReq) {
      this.startBatchTasks(
        issues,
        {
          user: getProperty('session', {}).id,
          assignee: assignToId || '',
          issueLink: issueLink,
        },
        API_ROUTES.issueAssign,
        issuesUpdated
      );
    } else {
      const payload = {
        user: getProperty('session', {}).id,
        assignee: assignToId,
        issueLink: issueLink,
        issues: issues,
      };

      return axios.post(API_ROUTES.issueAssign, payload, HTTP_CONFIG);
    }
  },

  changeState(issues: Issue[], state: string) {
    const payload = {
      user: getProperty('session', {}).id,
      newState: state,
      issues: issues,
    };

    return axios.post(API_ROUTES.issueState, payload, HTTP_CONFIG);
  },

  resolve(issues: Issue[], options: any, comment: string, batchReq?: boolean, issuesUpdated?: any) {
    if (batchReq) {
      this.startBatchTasks(
        issues,
        {
          user: getProperty('session', {}).id,
          newState: 'CLOSED',
          resolution: options,
          comment: comment,
        },
        API_ROUTES.issueState,
        issuesUpdated
      );
    } else {
      const payload = {
        user: getProperty('session', {}).id,
        newState: 'CLOSED',
        resolution: options,
        comment: comment,
        issues: issues,
      };

      return axios.post(API_ROUTES.issueState, payload, HTTP_CONFIG);
    }
  },

  comment(issues: Issue[], comment: string, batchReq?: boolean, issuesUpdated?: any) {
    if (batchReq) {
      this.startBatchTasks(
        issues,
        {
          user: getProperty('session', {}).id,
          comment: comment,
        },
        API_ROUTES.issueComment,
        issuesUpdated
      );
    } else {
      const payload = {
        user: getProperty('session', {}).id,
        comment: comment,
        issues: issues,
      };

      return axios.post(API_ROUTES.issueComment, payload, HTTP_CONFIG);
    }
  },

  defer(issues: Issue[], time: string) {
    const payload = {
      user: getProperty('session', {}).id,
      resolveByTime: moment.utc(time).format('YYYY-MM-DDTHH:mm:SS') + 'Z',
      issues: issues,
    };

    return axios.post(API_ROUTES.issueDefer, payload, HTTP_CONFIG);
  },

  async details(id: string) {
    const route = API_ROUTES.issueGet.replace(':id', id);

    try {
      const response: AxiosResponse<any> = await axios.get(route, HTTP_CONFIG);
      return { status: response.status, data: response.data };
    } catch (error) {
      const err = error as { response?: { status: number } };
      return { status: err.response?.status || 500, data: {} };
    }
  },
};

export const searchIssuesService = {
  fetch: (searchParams: string) => {
    const url = API_ROUTES.searchIssues.replace(':searchParams', searchParams);
    return axios
      .get(url, HTTP_CONFIG)
      .then((response: AxiosResponse<any>) => ({
        status: response.status,
        data: response.data,
      }))
      .catch((error: any) => ({
        status: error.response?.status || 500,
        data: { issues: [] },
      }));
  },

  assignees: () => {
    const url = API_ROUTES.issueAssignees;
    return axios.get(url, HTTP_CONFIG);
  },
};

export const SharedIssue = {
  getUsername(userId: string, usernames: Record<string, string>) {
    return usernames?.[userId] || userId;
  },
  processIssue: (
    issue: Issue,
    processFullDetails: boolean,
    usernames: Record<string, string>,
  ) => {
    const filterIssue = (issue: Issue, flag: string) =>
      issueFilter(issue, flag);
    const filterFormatFullDateTime = (createTime: string) =>
      formatFullDateTime(createTime);
    const timelineEventTypeFilters = (event: TimelineEvent) =>
      timelineEventTypeFilter(event);
    const timelineEventDetailHeaderFilters = (
      event: TimelineEvent,
      issue: Issue,
      userName: string,
    ) => timelineEventDetailHeaderFilter(event, issue, userName);
    const timelineEventIssueFilters = (event: TimelineEvent, flag: string) =>
      timelineEventIssueFilter(event, flag);

    issue.titleValue = filterIssue(issue, 'title');
    issue.noteValue = filterIssue(issue, 'note');
    issue.descriptionValue = filterIssue(issue, 'description');
    issue.typeValue = filterIssue(issue, 'type');
    issue.stateValue = filterIssue(issue, 'state');
    issue.assignedToUsernameValue = SharedIssue.getUsername(
      issue.assignedTo || '',
      usernames,
    );
    issue.resolutionValue = filterIssue(issue, 'resolution');
    issue.subResolutionValue = filterIssue(issue, 'subResolution');
    issue.extraMinutesAddedValue = filterIssue(issue, 'extraMinutesAdded');
    issue.extraMinutesAvoidedValue = filterIssue(issue, 'extraMinutesAvoided');
    issue.travelerContactedValue = filterIssue(issue, 'travelerContacted');
    issue.itineraryUpdatedValue = filterIssue(issue, 'itineraryUpdated');
    issue.overnightAvoidedValue = filterIssue(issue, 'overnightAvoided');
    issue.ratePaidInUsDollarsValue = filterIssue(issue, 'ratePaidInUsDollars');
    issue.flightsChangedValue = filterIssue(issue, 'flightsChanged');
    issue.hotelChangedValue = filterIssue(issue, 'hotelChanged');
    issue.newFlightsOnSameCarrierValue = filterIssue(
      issue,
      'newFlightsOnSameCarrier',
    );
    issue.groundTransportationChangedValue = filterIssue(
      issue,
      'groundTransportationChanged',
    );
    issue.optInTravelerValue = filterIssue(issue, 'optInTraveler');
    issue.creationDateValue = filterIssue(issue, 'creationDate');
    issue.resolveByTimeValue = filterIssue(issue, 'resolveByTime');

    if (issue.comments) {
      issue.comments = issue.comments.map((comment: any) => ({
        ...comment,
        createTimeFormatted: filterFormatFullDateTime(comment.createTime),
        byUsername: SharedIssue.getUsername(comment.user, usernames),
      }));
    }

    if (issue.messages) {
      issue.message = issue.messages.map((message: any) => ({
        ...message,
        createTimeFormatted: filterFormatFullDateTime(message.createTime),
        byUsername: SharedIssue.getUsername(message.changedByUserId, usernames),
      }));
    }

    if (processFullDetails && issue.timeline) {
      issue.timeline = issue.timeline.map((event: TimelineEvent) => {
        const showResolvedDetails = event.change === 'RESOLVED' || [
          'resolvedType',
          'resolvedPrettyType',
          'resolvedSubResolution',
          'resolvedOptInTraveler',
          'resolvedExtraMinutesAdded',
          'resolvedExtraMinutesAvoided',
          'resolvedTravelerContacted',
          'resolvedItineraryUpdated',
          'resolvedOvernightAvoided',
          'resolvedRatePaidInUsDollars',
          'resolvedFlightsChanged',
          'resolvedHotelChanged',
          'resolvedNewFlightsOnSameCarrier',
          'resolvedGroundTransportationChanged',
        ].some(key => key in event);

        const getUserDetails: any = SharedIssue.getUsername(
          'comment' in event
            ? event.user || ''
            : event.assigneeUserId || event.changedByUserId || '',
          usernames,
        );
        return {
          ...event,
          showResolvedDetails,
          createTimeFormatted: filterFormatFullDateTime(event.createTime),
          typeValue: timelineEventTypeFilters(event),
          detailTitle: timelineEventDetailHeaderFilters(
            event,
            issue,
            getUserDetails,
          ),
          resolvedState: timelineEventIssueFilters(event, 'state'),
          resolvedType: timelineEventIssueFilters(event, 'type'),
          resolvedPrettyType: timelineEventIssueFilters(event, 'resolution'),
          resolvedOptInTraveler: timelineEventIssueFilters(
            event,
            'optInTraveler',
          ),
          resolvedSubResolution: timelineEventIssueFilters(
            event,
            'subResolution',
          ),
          resolvedResolveByTime: timelineEventIssueFilters(
            event,
            'resolveByTime',
          ),
          resolvedExtraMinutesAdded: timelineEventIssueFilters(
            event,
            'extraMinutesAdded',
          ),
          resolvedExtraMinutesAvoided: timelineEventIssueFilters(
            event,
            'extraMinutesAvoided',
          ),
          resolvedTravelerContacted: timelineEventIssueFilters(
            event,
            'travelerContacted',
          ),
          resolvedItineraryUpdated: timelineEventIssueFilters(
            event,
            'itineraryUpdated',
          ),
          resolvedOvernightAvoided: timelineEventIssueFilters(
            event,
            'overnightAvoided',
          ),
          resolvedRatePaidInUsDollars: timelineEventIssueFilters(
            event,
            'ratePaidInUsDollars',
          ),
          resolvedFlightsChanged: timelineEventIssueFilters(
            event,
            'flightsChanged',
          ),
          resolvedHotelChanged: timelineEventIssueFilters(
            event,
            'hotelChanged',
          ),
          resolvedNewFlightsOnSameCarrier: timelineEventIssueFilters(
            event,
            'newFlightsOnSameCarrier',
          ),
          resolvedGroundTransportationChanged: timelineEventIssueFilters(
            event,
            'groundTransportationChanged',
          ),
        };
      });
    }
    return issue;
  },
};

export const paramsObject = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(searchParams.entries());
}